/**
 * Word-search grid generator — pure function, no external dependencies.
 * Used by the frontend. Canonical source; api/src/lib/wordsearchGrid.ts
 * must be kept in sync with this file.
 */

export type Difficulty = 'easy' | 'medium' | 'hard'
export type SearchMode = 'simple' | 'diagonals' | 'reversed'

export const DIFFICULTY_CONFIG: Record<Difficulty, { size: number; wordCount: number }> = {
  easy:   { size: 8,  wordCount: 5  },
  medium: { size: 12, wordCount: 8  },
  hard:   { size: 16, wordCount: 12 },
}

type Dir = [number, number]
const DIRS_SIMPLE:    Dir[] = [[0, 1], [1, 0]]
const DIRS_DIAGONALS: Dir[] = [[0, 1], [1, 0], [1, 1], [1, -1]]
const DIRS_REVERSED:  Dir[] = [[0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]]

export function getModeDirections(mode: SearchMode): Dir[] {
  if (mode === 'simple')    return DIRS_SIMPLE
  if (mode === 'diagonals') return DIRS_DIAGONALS
  return DIRS_REVERSED
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// Private-use placeholder swapped in before NFD so Ñ isn't stripped as a diacritic
const NTilde = '\uE000'

export function normalizeWord(word: string): string {
  // NFD decomposes Ñ → N + combining tilde, which would then be stripped.
  // Swap Ñ out for a private-use placeholder, normalize the rest, then restore.
  return word
    .replace(/[ñÑ]/g, NTilde)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .toUpperCase()
    .replace(new RegExp(NTilde, 'g'), 'Ñ')
}

export interface PlacedWord {
  word:       string   // original word, for display
  normalized: string   // uppercase, no accents/spaces, for grid matching
  row:        number
  col:        number
  dr:         number
  dc:         number
}

export interface GridData {
  cells:       string[][]
  placedWords: PlacedWord[]
  size:        number
}

const FILL_LETTERS = 'ABCDEFGHIJLMNÑOPRSTUVXYZ'

export function generateGrid(words: string[], difficulty: Difficulty, mode: SearchMode): GridData {
  const { size, wordCount } = DIFFICULTY_CONFIG[difficulty]
  const dirs = getModeDirections(mode)

  const candidates = shuffle(words)
    .map(w => ({ word: w, normalized: normalizeWord(w) }))
    .filter(({ normalized }) => normalized.length >= 3 && normalized.length <= size)

  const toPlace = candidates.slice(0, wordCount)

  const grid: string[][] = Array.from({ length: size }, () => Array(size).fill(''))
  const placedWords: PlacedWord[] = []

  for (const { word, normalized } of toPlace) {
    let placed = false
    for (let attempt = 0; attempt < 300 && !placed; attempt++) {
      const [dr, dc] = dirs[Math.floor(Math.random() * dirs.length)]
      const row = Math.floor(Math.random() * size)
      const col = Math.floor(Math.random() * size)

      const endRow = row + dr * (normalized.length - 1)
      const endCol = col + dc * (normalized.length - 1)
      if (endRow < 0 || endRow >= size || endCol < 0 || endCol >= size) continue

      let ok = true
      for (let i = 0; i < normalized.length && ok; i++) {
        const cell = grid[row + dr * i][col + dc * i]
        if (cell !== '' && cell !== normalized[i]) ok = false
      }
      if (!ok) continue

      for (let i = 0; i < normalized.length; i++) {
        grid[row + dr * i][col + dc * i] = normalized[i]
      }
      placedWords.push({ word, normalized, row, col, dr, dc })
      placed = true
    }
  }

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (grid[r][c] === '') {
        grid[r][c] = FILL_LETTERS[Math.floor(Math.random() * FILL_LETTERS.length)]
      }
    }
  }

  return { cells: grid, placedWords, size }
}
